/**
 * Wire enumerations from the DHA Partner Integration API Field Specification v1.4.1.
 *
 * The wire value is the source of truth here; every human-readable label lives in
 * the i18n catalogue. The spec's own example column renders several of these with
 * spaces or hyphens (`RE-ISSUE`, `SELF SERVICE`, `PO BOX`) while its "Options
 * include" lists use underscores — the underscore forms below are what we send,
 * and `normalizeEnum` below defends us on the way back in.
 */

/** Spec §4.1, §4.5, §4.10, §4.18 */
export const PRODUCT_CATEGORY = {
  ID: 'ID',
  PASSPORT: 'PASSPORT',
} as const
export type ProductCategory = (typeof PRODUCT_CATEGORY)[keyof typeof PRODUCT_CATEGORY]

/** Spec §4.5. Pilot phase 1 permits ID + RE_ISSUE only. */
export const PRODUCT_TYPE = {
  RE_ISSUE: 'RE_ISSUE',
  FIRST_APPLICATION: 'FIRST_APPLICATION',
  ADULT_TOURIST_PASSPORT: 'ADULT_TOURIST_PASSPORT',
  ADULT_TRAVEL_DOCUMENT: 'ADULT_TRAVEL_DOCUMENT',
  CREW_MEMBER_CARD: 'CREW_MEMBER_CARD',
  MAXI_ADULT_TOURIST_PASSPORT: 'MAXI_ADULT_TOURIST_PASSPORT',
  MAXI_MINOR_TOURIST_PASSPORT: 'MAXI_MINOR_TOURIST_PASSPORT',
  MINOR_TOURIST_PASSPORT: 'MINOR_TOURIST_PASSPORT',
  MINOR_TRAVEL_DOCUMENT: 'MINOR_TRAVEL_DOCUMENT',
} as const
export type ProductType = (typeof PRODUCT_TYPE)[keyof typeof PRODUCT_TYPE]

/** Spec §4.1 `Reasons`, §4.5 `Products.Reason` */
export const APPLICATION_REASON = {
  LOST: 'LOST',
  STOLEN: 'STOLEN',
  DAMAGED: 'DAMAGED',
  MARRIAGE: 'MARRIAGE',
  DIVORCE: 'DIVORCE',
  REPLACE_ID_BOOK: 'REPLACE_ID_BOOK',
} as const
export type ApplicationReason = (typeof APPLICATION_REASON)[keyof typeof APPLICATION_REASON]

/** Spec §4.5 `PersonalDetails.Gender` */
export const GENDER = {
  MALE: 'MALE',
  FEMALE: 'FEMALE',
} as const
export type Gender = (typeof GENDER)[keyof typeof GENDER]

/** Spec §4.5 `PersonalDetails.MaritalStatus` */
export const MARITAL_STATUS = {
  // Underscored to match normalizeEnum's output; the spec prints 'Never married'.
  NEVER_MARRIED: 'NEVER_MARRIED',
  MARRIED: 'MARRIED',
  WIDOWED: 'WIDOWED',
  DIVORCED: 'DIVORCED',
} as const
export type MaritalStatus = (typeof MARITAL_STATUS)[keyof typeof MARITAL_STATUS]

/**
 * Spec §4.5 — reasons that assert a change of marital state are only valid when the
 * NPR record agrees. Checking this client-side turns a server rejection several
 * steps later into an inline message on the field that caused it.
 */
export const REASON_REQUIRES_MARITAL_STATUS: Partial<Record<ApplicationReason, MaritalStatus>> = {
  MARRIAGE: MARITAL_STATUS.MARRIED,
  DIVORCE: MARITAL_STATUS.DIVORCED,
}

/** Spec §4.11 / §4.12 `StatusType` — the application lifecycle. */
export const APPLICATION_STATUS = {
  OUTSTANDING_REQUIREMENTS_PENDING: 'OUTSTANDING_REQUIREMENTS_PENDING',
  SUBMITTED: 'SUBMITTED',
  SENT_TO_PRINT: 'SENT_TO_PRINT',
  SHIPPED: 'SHIPPED',
  READY_FOR_COLLECTION: 'READY_FOR_COLLECTION',
  READY_FOR_RECEIPT: 'READY_FOR_RECEIPT',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
} as const
export type ApplicationStatus = (typeof APPLICATION_STATUS)[keyof typeof APPLICATION_STATUS]

/** Spec §4.10, §4.15 */
export const OUTSTANDING_REQUIREMENT = {
  PAYMENT: 'PAYMENT',
  BIO_ENROLMENT: 'BIO_ENROLMENT',
} as const
export type OutstandingRequirement =
  (typeof OUTSTANDING_REQUIREMENT)[keyof typeof OUTSTANDING_REQUIREMENT]

/** Spec §4.10 `Method` */
export const SUBMISSION_METHOD = {
  SELF_SERVICE: 'SELF_SERVICE',
  COUNTER: 'COUNTER',
  APP: 'APP',
} as const
export type SubmissionMethod = (typeof SUBMISSION_METHOD)[keyof typeof SUBMISSION_METHOD]

/** Spec §4.7 `PostalAddressDetails.PostalType` */
export const POSTAL_TYPE = {
  PO_BOX: 'PO_BOX',
  PRIVATE_BOX: 'PRIVATE_BOX',
} as const
export type PostalType = (typeof POSTAL_TYPE)[keyof typeof POSTAL_TYPE]

/**
 * Spec §4.5 `DeclarationsRequired`, §4.17.
 *
 * The full set the live §4.17 endpoint returns (fifteen codes as of 2026-09-08, not
 * the five v1.4.1 prints). The names are DHA's own `DeclarationName` values and are
 * recorded here as documentation only: the wording a citizen signs is ALWAYS the
 * `declarationWording` §4.17 sends, never anything hardcoded.
 *
 * DEC007–DEC014 are passport declarations. Pilot phase 1 is ID re-issue only, so
 * §4.5 will not name them today.
 *
 * ⚠ THIS MAP IS NOT A FILTER, and must never become one again. It was, until
 * `parseDeclarationsRequired` dropped DEC015 the day §4.5 started naming it: the
 * screen then showed and submitted two of the three declarations DHA required, §4.9
 * answered 204 anyway, and §4.10 failed three screens later with an opaque
 * `400 (Invalid or missing Declaration)`. What a code we do not know costs is
 * decided by the §4.17 wording join in selectDeclarations.ts, which blocks the
 * declaration screen and names the code. See docs/api-gaps.md #31.
 */
export const DECLARATION_CODE = {
  /** Confirmation of applicant details for Smart ID. */
  DEC001: 'DEC001',
  /** Replace Green ID Book. */
  DEC002: 'DEC002',
  /** Damaged/Destroyed identification document. */
  DEC003: 'DEC003',
  /** Lost Identity Document. */
  DEC004: 'DEC004',
  /** Stolen Identity Document. */
  DEC005: 'DEC005',
  /** Smart ID Collection. */
  DEC006: 'DEC006',
  /** Confirmation of applicant details for Passport. */
  DEC007: 'DEC007',
  /** First Passport Application. */
  DEC008: 'DEC008',
  /** Damaged / Destroyed Passport. */
  DEC009: 'DEC009',
  /** Lost Passport. */
  DEC010: 'DEC010',
  /** Stolen Passport. */
  DEC011: 'DEC011',
  /** Passport Full. */
  DEC012: 'DEC012',
  /** Second Passport. */
  DEC013: 'DEC013',
  /** Passport Collection. */
  DEC014: 'DEC014',
  /**
   * Application Cancellation.
   *
   * §4.5 began naming this alongside the re-issue declarations on 2026-09-08 —
   * on EVERY application, in an unstable position within the array. Its wording is
   * a cancellation request, which is not obviously something an applicant should
   * sign in order to apply; raised with DHA as docs/api-gaps.md #31.
   */
  DEC015: 'DEC015',
} as const
export type DeclarationCode = (typeof DECLARATION_CODE)[keyof typeof DECLARATION_CODE]

/**
 * Coerces the spec's inconsistent inbound rendering onto our wire values:
 * `"RE-ISSUE"`, `"Self Service"` and `"PO BOX"` all normalise to the underscore form.
 */
export function normalizeEnum(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_')
}

/** Spec §4.3 — the facial capture DHA accepts. */
export const FACE_IMAGE = {
  width: 560,
  height: 720,
  maxBytes: 4 * 1024 * 1024,
  mimeType: 'image/jpeg',
} as const

/** Spec §4.9 — the signature DHA prints on the card. */
export const SIGNATURE_IMAGE = {
  minWidth: 60,
  maxWidth: 864,
  minHeight: 60,
  maxHeight: 216,
  maxBytes: 48 * 1024,
  mimeType: 'image/jpeg',
} as const

/** ISO 3166-1 alpha-3, per every `Country` field in the spec. */
export const DEFAULT_COUNTRY = 'ZAF'
